import { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/shared/components/ui/Button'

interface DetailPageLayoutProps {
  title: string
  subtitle?: string
  breadcrumbs?: Array<{ label: string; to?: string }>
  onBack?: () => void
  backLabel?: string
  children: ReactNode
}

export function DetailPageLayout({
  title,
  subtitle,
  breadcrumbs = [],
  onBack,
  backLabel = 'Back',
  children,
}: DetailPageLayoutProps) {
  const navigate = useNavigate()
  const handleBack = onBack || (() => navigate(-1))

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Breadcrumbs */}
      {(breadcrumbs.length > 0 || onBack) && (
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-background">
          <div className="flex items-center gap-4">
            {onBack && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="h-8 w-8"
                aria-label={backLabel}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {breadcrumbs.length > 0 && (
              <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                  <span key={index} className="flex items-center gap-2">
                    {index > 0 && (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    {index === breadcrumbs.length - 1 || !crumb.to ? (
                      <span className="font-medium text-foreground">{crumb.label}</span>
                    ) : (
                      <Link
                        to={crumb.to}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {crumb.label}
                      </Link>
                    )}
                  </span>
                ))}
              </nav>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onBack && !breadcrumbs.length && (
              <Button variant="ghost" size="icon" onClick={handleBack} aria-label={backLabel}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Title Area */}
      <div className="px-6 py-4 border-b border-border bg-background">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
          {subtitle && (
            <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          )}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">{children}</div>
      </main>
    </div>
  )
}